/**
 * Phase B-3 — Assessment Result Panel.
 * Renders the verdict produced by the engine: risk band, KPIs, per-bank
 * breakdown, hard blockers, headline reasons.
 */
import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { CheckCircle2, AlertTriangle, XCircle, TrendingUp, Banknote, FileText } from 'lucide-react';

const BAND_META = {
  green: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2, label: 'HIGH likelihood — proceed to bank submission' },
  amber: { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle, label: 'MEDIUM — proceed with caution / shortlist selectively' },
  red: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'LOW likelihood — route to special_handling / outsource' },
};

const DECISION_BADGE = {
  approve: 'bg-emerald-100 text-emerald-700',
  caution: 'bg-amber-100 text-amber-700',
  reject: 'bg-red-100 text-red-700',
};

const fmtRM = (v) => v != null ? `RM ${Number(v).toLocaleString('en-MY', { maximumFractionDigits: 0 })}` : '—';

export default function AssessmentResultPanel({ result }) {
  const v = result.verdict || {};
  const band = v.risk_band || 'red';
  const meta = BAND_META[band];
  const Icon = meta.icon;

  return (
    <div className="space-y-4" data-testid="assessment-result-panel">
      {/* Risk band headline */}
      <div className={`p-4 rounded-xl border-2 flex items-center gap-3 ${meta.color}`} data-testid={`result-band-${band}`}>
        <Icon className="w-8 h-8 flex-shrink-0" />
        <div>
          <p className="font-bold uppercase text-lg">{band}</p>
          <p className="text-sm">{meta.label}</p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Qualified Income" value={fmtRM(v.qualified_income_total)} icon={TrendingUp} />
        <Kpi label="Best-bank DSR" value={`${v.overall_dsr_percent}%`} />
        <Kpi label="Est. installment" value={fmtRM(v.new_installment_estimate)} />
        <Kpi label="Recommended Loan" value={fmtRM(v.recommended_loan_amount)} icon={Banknote} />
      </div>

      {/* Hard blockers (when red) */}
      {v.hard_blockers && v.hard_blockers.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3">
          <p className="font-semibold text-red-800 text-sm mb-2 flex items-center gap-1"><XCircle className="w-4 h-4" /> Hard Blockers (all banks)</p>
          <ul className="text-sm space-y-1 text-red-700 list-disc ml-5">
            {v.hard_blockers.map((b, i) => <li key={i} data-testid={`blocker-${i}`}>{b}</li>)}
          </ul>
        </div>
      )}

      {/* Headline reasons (when green/amber) */}
      {v.headline_reasons && v.headline_reasons.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="font-semibold text-amber-800 text-sm mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Notes from top bank</p>
          <ul className="text-sm space-y-1 text-amber-700 list-disc ml-5">
            {v.headline_reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {/* Recommended banks */}
      {v.top_banks && v.top_banks.length > 0 && (
        <div className="space-y-2" data-testid="result-top-banks">
          <p className="text-sm font-semibold text-gray-700">Recommended Banks (ranked by approval probability)</p>
          {v.top_banks.map((b, i) => (
            <div key={b.bank_code} className="border rounded-lg p-3 bg-white" data-testid={`bank-verdict-${b.bank_code}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs text-gray-400 font-mono">#{i + 1}</span>
                <span className="font-semibold">{b.bank_name}</span>
                <Badge className={DECISION_BADGE[b.decision]}>{b.decision}</Badge>
                <span className="text-sm font-bold text-indigo-700 ml-auto">{b.approval_probability}% probability</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Tile label="DSR" value={`${b.dsr_percent}% / cap ${b.max_dsr_percent}%`} />
                <Tile label="Qualified income" value={fmtRM(b.qualified_income)} />
                <Tile label="Installment" value={fmtRM(b.monthly_installment)} />
                <Tile label="Max loan (this bank)" value={fmtRM(b.max_loan_amount)} />
              </div>
              {b.reasons && b.reasons.length > 0 && (
                <ul className="text-xs text-gray-600 mt-2 list-disc ml-5 space-y-0.5">
                  {b.reasons.map((r, j) => <li key={j}>{r}</li>)}
                </ul>
              )}
            </div>
          ))}
          {/* V4.24.14 — Regulatory disclaimer required alongside the recommended banks list. */}
          <p
            className="text-xs text-gray-500 italic mt-2 leading-relaxed"
            data-testid="recommended-banks-disclaimer"
          >
            The banks listed are recommendations only. Other banks not listed may
            also provide home loan offers, subject to their assessment and approval.
          </p>
        </div>
      )}

      {/* Excluded banks (collapsible by default — just a small section) */}
      {v.excluded_banks && v.excluded_banks.length > 0 && (
        <details className="border rounded-lg bg-slate-50 p-3" data-testid="result-excluded-banks">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700">
            Excluded banks ({v.excluded_banks.length}) — why they won't fit
          </summary>
          <div className="mt-2 space-y-2">
            {v.excluded_banks.map(b => (
              <div key={b.bank_code} className="border-l-2 border-red-300 pl-2 text-xs">
                <p className="font-medium">{b.bank_name}</p>
                <ul className="list-disc ml-4 text-red-700">
                  {(b.blockers || []).map((bl, i) => <li key={i}>{bl}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Evidence + provider footer */}
      <div className="flex items-center gap-3 text-xs text-gray-500 border-t pt-3">
        <span>Provider: <span className="font-mono text-indigo-600">{result.provider_used}</span></span>
        <span>Run by: {result.run_by_name || result.run_by}</span>
        <span>Run at: {result.created_at ? new Date(result.created_at).toLocaleString('en-MY') : '—'}</span>
        {result.ctos_pdf_url && (
          <a
            href={result.ctos_pdf_url.startsWith('http') ? result.ctos_pdf_url : `${process.env.REACT_APP_BACKEND_URL}/api${result.ctos_pdf_url}`}
            target="_blank" rel="noreferrer"
            className="ml-auto text-indigo-600 hover:underline flex items-center gap-1"
            data-testid="result-ctos-evidence"
          ><FileText className="w-3 h-3" /> View uploaded CTOS</a>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg bg-slate-100 p-3">
      <p className="text-xs text-slate-600 uppercase flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded p-2 bg-slate-50">
      <p className="text-[10px] uppercase text-slate-500">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}
